import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, FileSpreadsheet, Download, UploadCloud, CheckCircle2, X } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, TH } from "@/components/common";
import { api, apiError, download, fmtQty } from "@/lib/api";

const STATUS = {
  nouveau: ["Nouveau", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  mise_a_jour: ["Mise à jour", "bg-blue-50 text-blue-700 border-blue-200"],
  erreur: ["Erreur", "bg-rose-50 text-rose-700 border-rose-200"],
};

function Stat({ label, value, testid, cls = "" }) {
  return <div className={`rounded border px-4 py-3 ${cls}`}><div className="text-xs uppercase tracking-wider text-slate-500">{label}</div><div className="font-mono text-2xl font-bold" data-testid={testid}>{value}</div></div>;
}

export default function ImportPage() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [upsert, setUpsert] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const analyse = async (f = file, u = upsert) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("upsert", u ? "true" : "false");
    try {
      setPreview((await api.post("/import/preview", fd)).data);
    } catch (e) {
      setPreview(null);
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const pick = (f) => { setFile(f); setPreview(null); if (f) analyse(f, upsert); };
  const toggleUpsert = (v) => { setUpsert(v); if (file) analyse(file, v); };
  const cancel = () => { setFile(null); setPreview(null); if (inputRef.current) inputRef.current.value = ""; };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/commit", { rows: preview.rows.filter((r) => r.status !== "erreur").map((r) => ({ ...r, stock: r.stock, seuil_min: r.seuil_min })), upsert });
      toast.success(`${data.imported} article(s) importé(s), ${data.stock_movements} mouvement(s) de stock créé(s).`);
      cancel();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const valid = preview ? preview.summary.nouveaux + preview.summary.mises_a_jour : 0;

  return (
    <div data-testid="import-page">
      <PageHeader title="Import / Export" subtitle="Importez vos articles depuis un fichier CSV ou Excel, avec contrôle avant validation." testid="import-title">
        <Button variant="outline" className="h-12 gap-2" onClick={() => download("/import/template?format=csv", "modele_articles.csv")} data-testid="template-csv-button"><Download className="h-4 w-4" /> Modèle CSV</Button>
        <Button variant="outline" className="h-12 gap-2" onClick={() => download("/import/template?format=xlsx", "modele_articles.xlsx")} data-testid="template-xlsx-button"><FileSpreadsheet className="h-4 w-4" /> Modèle Excel</Button>
        <Button className="h-12 gap-2" onClick={() => download("/export/articles", "articles_stockkit.csv")} data-testid="export-csv-button"><Download className="h-4 w-4" /> Exporter les articles</Button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}
          className="flex min-h-[200px] cursor-pointer flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed border-slate-300 bg-white p-8 text-center transition-colors hover:border-orange-400 hover:bg-orange-50/40 lg:col-span-2" data-testid="import-dropzone">
          <UploadCloud className="h-10 w-10 text-slate-400" />
          <div className="font-display text-2xl font-bold uppercase">{file ? file.name : "Déposez votre fichier ici"}</div>
          <div className="text-sm text-slate-500">ou cliquez pour choisir un fichier .csv ou .xlsx (5 Mo max)</div>
          <input ref={inputRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="import-file-input" />
        </label>
        <div className="space-y-4 rounded-md border bg-white p-6 shadow-sm">
          <label className="flex cursor-pointer items-start gap-3">
            <Switch checked={upsert} onCheckedChange={toggleUpsert} className="mt-0.5" data-testid="import-upsert-switch" />
            <span><span className="block font-semibold">Mettre à jour les articles existants</span><span className="text-sm text-slate-500">Les codes déjà présents sont mis à jour. Un stock différent génère un ajustement d'inventaire.</span></span>
          </label>
          <div className="text-sm text-slate-500">Colonnes : <span className="font-mono text-xs">code ; designation ; code_barres ; unite ; emplacement ; categorie ; seuil_min ; stock</span></div>
        </div>
      </div>

      {busy && !preview && <p className="mt-6 text-slate-500" data-testid="import-loading">Analyse du fichier…</p>}
      {preview && (
        <Panel title="Aperçu de l'import" icon={FileUp} testid="import-preview" className="mt-6"
          actions={<div className="flex gap-2">
            <Button variant="outline" className="h-11 gap-2" onClick={cancel} data-testid="import-cancel-button"><X className="h-4 w-4" /> Annuler</Button>
            <Button className="h-11 gap-2" disabled={!valid || busy} onClick={commit} data-testid="import-commit-button"><CheckCircle2 className="h-4 w-4" /> Valider l'import ({valid})</Button>
          </div>}>
          <div className="grid grid-cols-2 gap-3 p-5 md:grid-cols-4">
            <Stat label="Lignes" value={preview.summary.total} testid="import-stat-total" />
            <Stat label="Nouveaux" value={preview.summary.nouveaux} testid="import-stat-new" cls="bg-emerald-50/50" />
            <Stat label="Mises à jour" value={preview.summary.mises_a_jour} testid="import-stat-update" cls="bg-blue-50/50" />
            <Stat label="Erreurs (ignorées)" value={preview.summary.erreurs} testid="import-stat-errors" cls="bg-rose-50/50" />
          </div>
          <div className="max-h-[480px] overflow-auto border-t">
            <Table data-testid="import-preview-table">
              <TableHeader><TableRow>
                <TableHead className={TH}>Ligne</TableHead><TableHead className={TH}>Statut</TableHead><TableHead className={TH}>Code</TableHead><TableHead className={TH}>Désignation</TableHead>
                <TableHead className={TH}>Code-barres</TableHead><TableHead className={TH}>Catégorie</TableHead><TableHead className={`${TH} text-right`}>Seuil</TableHead><TableHead className={`${TH} text-right`}>Stock</TableHead><TableHead className={TH}>Erreurs</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {preview.rows.map((r) => (
                  <TableRow key={r.line} className={r.status === "erreur" ? "bg-rose-50/60" : ""} data-testid={`import-row-${r.line}`}>
                    <TableCell className="font-mono text-xs">{r.line}</TableCell>
                    <TableCell><span className={`rounded border px-2 py-0.5 text-xs font-semibold ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span></TableCell>
                    <TableCell className="font-mono font-semibold">{r.code || "—"}</TableCell>
                    <TableCell>{r.designation || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.code_barres || "—"}</TableCell>
                    <TableCell className="text-sm">{r.categorie || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{r.seuil_min ?? "—"}</TableCell>
                    <TableCell className="whitespace-nowrap text-right font-mono">{r.current_stock != null && r.stock != null && r.stock !== r.current_stock ? `${fmtQty(r.current_stock)} → ` : ""}{r.stock ?? "—"}</TableCell>
                    <TableCell className="text-sm text-rose-700" data-testid={`import-row-errors-${r.line}`}>{r.errors.join(" · ")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Panel>
      )}
    </div>
  );
}
